import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MEMBERSHIP_ROLE_MAP,
  WORK_PROFILES_MAX,
  workProfileRoles,
  type AccessProfileView,
  type MembershipRole,
  type SubAccountView,
  type WorkModeView,
} from '@goyone/shared';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { Icon } from './Icon';
import { Modal } from './ui';
import { useToast } from './Toast';

/**
 * الحساباتُ الفرعيّة — «أشتغل سيلز بنفس الدخول، كحسابٍ مستقلّ».
 *
 * ثلاث قطع: شارةٌ في الشريط تقول «أنت الآن في حساب سيلز» وترجعك بنقرة،
 * ومبدِّلٌ في قائمة الحساب يعرض كلَّ حسابٍ بملفّ صلاحياته ومسمّاه، ونافذةٌ
 * في شاشة المستخدمين يضيف بها المالكُ وحدَه حساباتِ كلِّ عضوٍ (ونفسِه).
 * والقرارُ كلُّه على الخادم: الواجهةُ تعرض وتطلب، لا تمنح.
 */

const roleLabel = (r: MembershipRole): string => MEMBERSHIP_ROLE_MAP[r]?.label ?? r;

/** «مندوب مبيعات · Sales Specialist» — ما يميّز الحسابَ عن إخوته. */
export function subAccountMeta(s: Pick<SubAccountView, 'role' | 'profileName' | 'jobTitle'>): string {
  return [s.profileName ?? roleLabel(s.role), s.jobTitle].filter(Boolean).join(' · ');
}

/**
 * التبديلُ ثمّ الرجوعُ إلى الرئيسيّة وإعادةُ كلِّ الاستعلامات: الشاشةُ
 * المفتوحةُ قد لا تُتاح في الحساب الجديد، والقوائمُ المحفوظةُ لحسابٍ آخر.
 */
export function useWorkSwitch() {
  const { switchWorkProfile } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const switchTo = async (id: string | null, label: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await switchWorkProfile(id);
      navigate('/');
      await queryClient.resetQueries();
      toast(id ? `تعمل الآن بالحساب الفرعي «${label}»` : 'رجعتَ إلى حسابك الأصليّ', 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'تعذّر التبديل');
    } finally {
      setBusy(false);
    }
  };

  return { switchTo, busy };
}

/** شارةُ الشريط — لا تظهر إلا والجلسةُ على حسابٍ فرعيّ، والنقرُ يرجعها. */
export function WorkModeBadge({ workMode }: { workMode?: WorkModeView }) {
  const { switchTo, busy } = useWorkSwitch();
  const active = workMode?.active;
  if (!active) return null;
  return (
    <button
      type="button"
      className="work-badge"
      onClick={() => void switchTo(null, workMode.realName)}
      disabled={busy}
      title={`أنت تعمل الآن بالحساب الفرعي «${active.name}» (${subAccountMeta(active)}) — انقر للرجوع إلى حسابك الأصليّ`}
    >
      <Icon name="briefcase" size={14} />
      <span>
        حساب فرعي: {active.name}
        <span className="work-badge-meta"> · {subAccountMeta(active)}</span>
      </span>
      <span className="work-badge-back">
        <Icon name="arrowRightLeft" size={12} />
        رجوع
      </span>
    </button>
  );
}

/** المبدِّلُ في قائمة الحساب — لمن أضاف له المالكُ حسابًا فرعيًّا أو أكثر. */
export function WorkModeSwitch({
  workMode,
  onDone,
}: {
  workMode?: WorkModeView;
  onDone: () => void;
}) {
  const { switchTo, busy } = useWorkSwitch();
  if (!workMode) return null;
  const { realRole, realName, active, profiles } = workMode;
  if (!active && !profiles.length) return null;

  const pick = (id: string | null, label: string) => {
    onDone();
    void switchTo(id, label);
  };

  return (
    <div className="pf-work">
      <div className="pf-work-title">الحسابات</div>
      <button
        type="button"
        className={`pf-row pf-work-row${!active ? ' is-on' : ''}`}
        disabled={busy || !active}
        onClick={() => pick(null, realName)}
      >
        <Icon name={!active ? 'check' : 'user'} size={15} />
        <span className="pf-work-text">
          <span className="pf-work-name">{realName} — الحساب الأصليّ</span>
          <span className="tiny mute">{roleLabel(realRole)}</span>
        </span>
      </button>
      {profiles.map((p) => (
        <button
          key={p.id}
          type="button"
          className={`pf-row pf-work-row${active?.id === p.id ? ' is-on' : ''}`}
          disabled={busy || active?.id === p.id}
          onClick={() => pick(p.userId, p.name)}
        >
          <Icon name={active?.id === p.id ? 'check' : 'briefcase'} size={15} />
          <span className="pf-work-text">
            <span className="pf-work-name">{p.name}</span>
            <span className="tiny mute">{subAccountMeta(p)}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

interface Draft {
  name: string;
  role: MembershipRole;
  profileId: string | null;
  jobTitle: string;
}

/**
 * نافذةُ الحسابات الفرعيّة لعضو — من شاشة المستخدمين، للمالك وحدَه.
 *
 * كلُّ حسابٍ فرعيٍّ مستخدمٌ مستقلّ: اسمه يظهر في قوائم المسؤولين، وعملاؤه
 * له وحده. ولكلٍّ دورٌ لا يعلو على دور صاحبه، وملفُّ صلاحيات، ومسمّى.
 */
export function SubAccountsDialog({
  member,
  onClose,
}: {
  member: { id: string; userId: string; name: string; role: MembershipRole };
  onClose: () => void;
}) {
  const { session, reload } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const current = useQuery({
    queryKey: ['team', 'sub-accounts', member.id],
    queryFn: () =>
      api<{ role: MembershipRole; profiles: SubAccountView[] }>(
        `/team/members/${member.id}/sub-accounts`,
      ),
  });
  const accessProfiles = useQuery({
    queryKey: ['team', 'profiles'],
    queryFn: () => api<AccessProfileView[]>('/team/profiles'),
    staleTime: 60_000,
  });

  const roles = workProfileRoles(member.role);
  const options = accessProfiles.data ?? [];
  const list = current.data?.profiles ?? [];

  /* صفٌّ يُحرَّر: معرّفُ حسابٍ قائم، أو «new» لحسابٍ جديد. */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  const done = async (msg: string) => {
    await queryClient.invalidateQueries({ queryKey: ['team'] });
    /* حساباتُه هو؟ — تُعاد قراءةُ جلسته ليتحدّث المبدِّلُ في قائمته فورًا. */
    if (member.userId === session?.user.id || member.userId === session?.workMode?.active?.userId) {
      await reload();
    }
    setEditing(null);
    setDraft(null);
    toast(msg, 'success');
  };

  const body = (d: Draft) => ({
    name: d.name.trim(),
    role: d.role,
    profileId: d.profileId,
    jobTitle: d.jobTitle.trim() || null,
  });

  const save = useMutation({
    mutationFn: () =>
      editing === 'new'
        ? api(`/team/members/${member.id}/sub-accounts`, { method: 'POST', body: body(draft!) })
        : api(`/team/sub-accounts/${editing}`, { method: 'PATCH', body: body(draft!) }),
    onSuccess: () => done(editing === 'new' ? 'أُضيف الحساب الفرعي' : 'حُفظ الحساب الفرعي'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/team/sub-accounts/${id}`, { method: 'DELETE' }),
    onSuccess: () => done('حُذف الحساب الفرعي'),
  });

  const startNew = () => {
    /* اقتراحٌ أوّل: ملفُّ المبيعات إن وُجد بدوره — يرى عملاءه وحدَه. */
    const sales = options.find((o) => /مبيعات|سيلز|sales/i.test(o.name));
    const role: MembershipRole =
      sales && roles.includes(sales.baseRole)
        ? sales.baseRole
        : roles.includes('EMPLOYEE')
          ? 'EMPLOYEE'
          : roles[0]!;
    setEditing('new');
    setDraft({
      name: `${member.name} (${sales?.name ?? 'سيلز'})`,
      role,
      profileId: sales?.id ?? null,
      jobTitle: '',
    });
  };

  const startEdit = (s: SubAccountView) => {
    setEditing(s.id);
    setDraft({ name: s.name, role: s.role, profileId: s.profileId, jobTitle: s.jobTitle ?? '' });
  };

  const err = save.error ?? remove.error ?? current.error;

  const form = draft && (
    <div className="wp-form">
      <div className="wp-row">
        <label className="field">
          <span className="label">الاسم (يظهر في قوائم المسؤولين)</span>
          <input
            className="input"
            value={draft.name}
            maxLength={120}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="label">المسمّى الوظيفي</span>
          <input
            className="input"
            value={draft.jobTitle}
            maxLength={120}
            placeholder="Sales Specialist"
            onChange={(e) => setDraft({ ...draft, jobTitle: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="label">ملفّ الصلاحيات</span>
          <select
            className="select"
            value={draft.profileId ?? ''}
            onChange={(e) => {
              const id = e.target.value || null;
              const p = options.find((o) => o.id === id);
              setDraft({
                ...draft,
                profileId: id,
                /* الملفُّ يقترح دورَه الأساسيّ إن لم يعلُ على دور صاحبه. */
                ...(p && roles.includes(p.baseRole) ? { role: p.baseRole } : {}),
              });
            }}
          >
            <option value="">بلا ملف — الدور وحده</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">الدور</span>
          <select
            className="select"
            value={draft.role}
            onChange={(e) => setDraft({ ...draft, role: e.target.value as MembershipRole })}
          >
            {roles
              .filter((x) => MEMBERSHIP_ROLE_MAP[x].assignable || x === draft.role)
              .map((x) => (
                <option key={x} value={x}>
                  {roleLabel(x)}
                </option>
              ))}
          </select>
        </label>
      </div>
      <div className="wp-actions">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={save.isPending || draft.name.trim().length < 2}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'جارٍ الحفظ' : editing === 'new' ? 'إضافة' : 'حفظ'}
        </button>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => {
            setEditing(null);
            setDraft(null);
          }}
        >
          إلغاء
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      title={`الحسابات الفرعية — ${member.name}`}
      onClose={onClose}
      footer={
        <button className="btn" onClick={onClose}>
          إغلاق
        </button>
      }
    >
      <p className="mute" style={{ marginTop: 0 }}>
        كلّ حساب فرعي حسابٌ مستقلّ: يظهر باسمه في قوائم المسؤولين، وعملاؤه وصفقاته له وحده. يدخله
        صاحبه بنفس بريده وكلمة مروره ثمّ يبدّل إليه من صورته أعلى الشاشة، ويرجع بنقرة. ولا يعلو دوره
        على دور صاحبه ({roleLabel(member.role)}).
      </p>

      {err && (
        <div className="alert alert-danger mb-4">
          {err instanceof Error ? err.message : 'تعذّرت العملية.'}
        </div>
      )}

      {current.isSuccess && list.length === 0 && editing !== 'new' && (
        <div className="mute tiny mb-4">لا حسابات فرعية بعد.</div>
      )}

      <div className="wp-list">
        {list.map((s) =>
          editing === s.id ? (
            <div key={s.id}>{form}</div>
          ) : (
            <div className="wp-item" key={s.id}>
              <Icon name="briefcase" size={16} />
              <span className="wp-item-text">
                <strong>{s.name}</strong>
                <span className="tiny mute">
                  {subAccountMeta(s)} · {roleLabel(s.role)}
                </span>
              </span>
              <button
                type="button"
                className="btn btn-icon btn-ghost"
                aria-label="تعديل"
                title="تعديل"
                disabled={!!editing}
                onClick={() => startEdit(s)}
              >
                <Icon name="edit" size={15} />
              </button>
              <button
                type="button"
                className="btn btn-icon btn-ghost"
                aria-label="حذف"
                title="حذف"
                disabled={!!editing || remove.isPending}
                onClick={() => {
                  if (window.confirm(`حذف الحساب الفرعي «${s.name}»؟ عملاؤه يبقون باسمه.`)) {
                    remove.mutate(s.id);
                  }
                }}
              >
                <Icon name="trash" size={15} />
              </button>
            </div>
          ),
        )}
        {editing === 'new' && form}
      </div>

      {!editing && (
        <div className="wp-actions">
          <button
            type="button"
            className="btn btn-sm"
            onClick={startNew}
            disabled={!current.isSuccess || list.length >= WORK_PROFILES_MAX}
          >
            <Icon name="plus" size={14} />
            إضافة حساب فرعي
          </button>
        </div>
      )}
    </Modal>
  );
}
